import { useCallback, useEffect, useLayoutEffect, useRef, useState } from 'react';
const ROW_HEIGHT_PX = 48;
const DEFAULT_HEIGHT_PX = ROW_HEIGHT_PX * 4;
export const useRowLayout = ({ headerRef, isLoading, isPaginationEnabled, rowCount, rowsRef, tableRef, }) => {
    const [tableHeight, setTableHeight] = useState(isPaginationEnabled ? DEFAULT_HEIGHT_PX : undefined);
    const [headerHeight, setHeaderHeight] = useState(0);
    const lastStableHeightRef = useRef(isPaginationEnabled ? DEFAULT_HEIGHT_PX : undefined);
    const measure = useCallback(() => {
        const header = headerRef.current;
        const table = tableRef.current;
        const rows = rowsRef.current;
        const newHeaderHeight = header ? header.getBoundingClientRect().height : 0;
        if (!table || !rows) {
            return { tableHeight: 0, headerHeight: newHeaderHeight };
        }
        const tbody = table.querySelector('tbody');
        const bodyHeight = tbody
            ? Array.from(tbody.children).reduce((acc, child) => {
                return acc + child.getBoundingClientRect().height;
            }, 0)
            : 0;
        const newTableHeight = newHeaderHeight + bodyHeight;
        return { tableHeight: newTableHeight, headerHeight: newHeaderHeight };
    }, [headerRef, tableRef, rowsRef]);
    const updateHeight = useCallback((newTableHeight, newHeaderHeight) => {
        if (typeof newHeaderHeight === 'number' && newHeaderHeight >= 0) {
            setHeaderHeight((prev) => (prev !== newHeaderHeight ? newHeaderHeight : prev));
        }
        if (newTableHeight === undefined || newTableHeight < 0) {
            return;
        }
        if (isLoading) {
            const last = lastStableHeightRef.current ?? newTableHeight;
            const clamped = Math.max(newTableHeight, last);
            setTableHeight((prev) => (prev !== clamped ? clamped : prev));
        }
        else {
            lastStableHeightRef.current = newTableHeight;
            setTableHeight((prev) => (prev !== newTableHeight ? newTableHeight : prev));
        }
    }, [isLoading]);
    const measureAndUpdateHeight = useCallback(() => {
        const { tableHeight: newTableHeight, headerHeight: newHeaderHeight } = measure();
        updateHeight(newTableHeight, newHeaderHeight);
    }, [measure, updateHeight]);
    useEffect(() => {
        measureAndUpdateHeight();
    }, [isLoading, rowCount]);
    useLayoutEffect(() => {
        const headerElement = headerRef.current;
        const rowsElement = rowsRef.current;
        const tableElement = tableRef.current;
        if (!rowsElement || !tableElement || !headerElement || typeof window === 'undefined') {
            return undefined;
        }
        let resizeObserver;
        if ('ResizeObserver' in window) {
            resizeObserver = new ResizeObserver(() => {
                measureAndUpdateHeight();
            });
            resizeObserver.observe(rowsElement);
            resizeObserver.observe(tableElement);
            resizeObserver.observe(headerElement);
        }
        let mutationObserver;
        if ('MutationObserver' in window) {
            mutationObserver = new MutationObserver(() => {
                measureAndUpdateHeight();
            });
            mutationObserver.observe(rowsElement, { childList: true, subtree: true });
        }
        return () => {
            resizeObserver?.disconnect();
            mutationObserver?.disconnect();
        };
    }, [headerRef, isLoading, measure, tableRef, updateHeight]);
    return {
        tableHeight,
        headerHeight,
    };
};
//# sourceMappingURL=useRowLayout.js.map